import { Component, Injector } from '@angular/core';
import {
  pendingUntilEvent,
  takeUntilDestroyed,
} from '@angular/core/rxjs-interop';
import { ɵAngularFireSchedulers } from '@angular/fire';
import { MatIconRegistry } from '@angular/material/icon';
import {
  DomSanitizer,
  SafeResourceUrl,
  Title,
} from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { AuthService } from '@principle-theorem/ng-auth';
import { HotkeysService } from '@principle-theorem/ng-principle';
import {
  AppVersionService,
  DatabaseUsageTrackerService,
  DynamicTitleService,
} from '@principle-theorem/ng-shared';
import {
  PRINCIPLE_CORE_SERIALISERS,
  RootConfig,
} from '@principle-theorem/principle-core';
import { AppName } from '@principle-theorem/principle-core/interfaces';
import {
  FUNCTIONS_REGION,
  FirebaseFunctionsScheduler,
  Firestore,
  FirestoreScheduler,
  SerialisationProvider,
} from '@principle-theorem/shared';
import { getApp } from 'firebase/app';
import { filter, map } from 'rxjs/operators';
import { environment } from '../environments/environment';

export const PRINCIPLE_SERIALISERS = PRINCIPLE_CORE_SERIALISERS;

@Component({
  imports: [RouterModule],
  selector: 'pr-app',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  constructor(
    private _iconRegistry: MatIconRegistry,
    private _domSanitizer: DomSanitizer,
    dynamicTitle: DynamicTitleService,
    title: Title,
    private _hotkeysService: HotkeysService,
    usageTracker: DatabaseUsageTrackerService,
    injector: Injector,
    private _authService: AuthService,
    versionService: AppVersionService
  ) {
    this._registerIcons([
      { label: 'principle-watermark', file: 'principle-watermark-logo.svg' },
      { label: 'principle-logo', file: 'principle-logo.svg' },
      { label: 'principle-row-white', file: 'principle-row-white.svg' },
      { label: 'principle-stacked-white', file: 'principle-stacked-white.svg' },
    ]);
    this._registerSvgIconSets([`symbol-defs.svg`]);
    this._iconRegistry.registerFontClassAlias(
      'material-symbols',
      'material-symbols-outlined'
    );

    dynamicTitle.default = 'Principle Partner Portal';
    dynamicTitle.title$
      .pipe(takeUntilDestroyed())
      .subscribe((value: string) => title.setTitle(value));

    const scheduler = new ɵAngularFireSchedulers();

    FirestoreScheduler.init(
      scheduler.outsideAngular,
      pendingUntilEvent(injector),
      getApp().name,
      environment.firebase
    );

    FirebaseFunctionsScheduler.init(
      scheduler.outsideAngular,
      FUNCTIONS_REGION,
      environment.firebase,
      environment.useEmulator ? ['localhost', 5001] : false
    );

    SerialisationProvider.init(PRINCIPLE_SERIALISERS);

    this._hotkeysService.registerHotkeys();

    if (environment.production) {
      const public$ = Firestore.doc$(RootConfig.public()).pipe(
        map((config) => config.versions?.[AppName.Referrals]),
        filter((version): version is string => !!version)
      );
      const logoutFn = (): Promise<void> => this._authService.logout();
      versionService.watchForUpdates(public$, logoutFn);
    }

    usageTracker.track();
  }

  private _registerIcons(icons: IIconAsset[]): void {
    icons.map((icon: IIconAsset) => {
      const path: SafeResourceUrl =
        this._domSanitizer.bypassSecurityTrustResourceUrl(
          `assets/icons/${icon.file}`
        );
      this._iconRegistry.addSvgIconInNamespace('assets', icon.label, path);
    });
  }

  private _registerSvgIconSets(iconSetFiles: string[]): void {
    iconSetFiles.map((file: string) => {
      const path: SafeResourceUrl =
        this._domSanitizer.bypassSecurityTrustResourceUrl(
          `assets/icons/${file}`
        );
      this._iconRegistry.addSvgIconSet(path);
    });
  }
}

interface IIconAsset {
  label: string;
  file: string;
}
